import {
  Text,
  Button,
  useToast,
  DefaultAlertDialog,
} from '~/common/components/defaultUIComponents';
import {
  Flex,
  Grid,
  GridItem,
  IconButton,
  Select,
  useDisclosure,
} from '@chakra-ui/react';
import JobTextRecordApi from '~/common/api/zeiterfassungServerApi/JobTextRecordApi';
import EmployeeApi from '~/common/api/zeiterfassungServerApi/EmployeeApi';
import ProjectApi from '~/common/api/zeiterfassungServerApi/ProjectApi';
import { ArrowForwardIcon, ArrowBackIcon } from '@chakra-ui/icons';
import { formatName } from '~/common/utils/employeeUtils';
import { TextRecordListItem } from './TextRecordListItem';
import { ListView } from '~/common/components/ListView';
import { useQueryClient } from '@tanstack/react-query';
import useTranslate from '~/common/utils/translate';
import React, { createRef, useState } from 'react';
import _ from 'lodash';

type FilterState = {
  employee?: number;
  project?: number;
  nullEmployee?: boolean;
};

export const TextRecordsList: React.FC = () => {
  const queryClient = useQueryClient();
  const t = useTranslate();
  const [filterState, setFilterState] = useState<FilterState>({});
  const employeeRef = createRef<HTMLSelectElement>();
  const projectRef = createRef<HTMLSelectElement>();
  const [page, setPage] = useState(0);
  const [{ data: recordCount }] = JobTextRecordApi.hooks.useTextRecordCount({
    refetchInterval: 1000 * 60,
  });
  const [{ data: textRecords, isLoading }, refetch] =
    JobTextRecordApi.hooks.useTextRecords(page, filterState, {
      enabled: recordCount && recordCount.count >= 0,
    });

  const handleClick = () => {
    setFilterState((filterState) => {
      const newFilter = { ...filterState };
      if (employeeRef.current && projectRef.current) {
        if (employeeRef.current.value === 'deleted') {
          newFilter.nullEmployee = true;
          newFilter.employee = undefined;
        } else if (employeeRef.current.value && employeeRef.current.value !== '-1') {
          newFilter.employee = parseInt(employeeRef.current.value);
          newFilter.nullEmployee = undefined;
        } else {
          newFilter.employee = undefined;
          newFilter.nullEmployee = undefined;
        }
        if (projectRef.current.value && projectRef.current.value !== '-1') {
          newFilter.project = parseInt(projectRef.current.value);
        } else {
          newFilter.project = undefined;
        }
      }
      return newFilter;
    });
  };
  const { isOpen, onToggle } = useDisclosure();

  const resetFilters = () => {
    setFilterState({});
    if (employeeRef.current && projectRef.current) {
      employeeRef.current.value = '-1';
      projectRef.current.value = '-1';
    }
  };
  const { mutateAsync: bulkDelete, isLoading: isDeleting } =
    JobTextRecordApi.hooks.useBulkDeleteJobTextRecord(page);
  const [{ data: employees }] = EmployeeApi.hooks.useEmployeeQuery();
  const [{ data: project }] = ProjectApi.hooks.useProjectsQuery();

  const [prevRecordCount, setPrevRecordCount] = useState<number | null>(null);

  React.useEffect(() => {
    if (
      recordCount &&
      textRecords &&
      prevRecordCount !== null &&
      prevRecordCount !== recordCount.count &&
      !isLoading
    ) {
      void refetch();
    }
    setPrevRecordCount(recordCount?.count || null);
  }, [recordCount]);
  const toast = useToast({});

  const handleDeleteClick = async () => {
    const { employee, project } = filterState;
    if (employee || project) {
      await bulkDelete({ employee, project }).then(onToggle);
      toast({ title: 'deletion.success', status: 'success' });
      void queryClient.invalidateQueries(['COUNT']);
    }
  };
  return (
    <>
      <DefaultAlertDialog
        dialogHeaderText={'dashboard.newtext.bulkDelete.confirmation.header'}
        dialogBodyText={'dashboard.newtext.bulkDelete.confirmation'}
        isOpen={isOpen}
        onConfirmedAction={() =>
          void handleDeleteClick().catch(() =>
            toast({ title: 'generic.error', status: 'error' }),
          )
        }
        onCancel={onToggle}
        isLoading={isDeleting}
      />
      <Flex direction="row" justify="space-between" mb={3}>
        <Flex gap={3}>
          <Select
            ref={employeeRef}
            style={{
              width: '145px',
            }}
          >
            <option value="-1">{t('selectEmployee')}</option>
            <option value="deleted">{t('dashboard.newtext.deleted.employee')}</option>
            {_.orderBy(employees, [(e) => formatName(e)], ['asc'])?.map((e) => (
              <option key={e.id} value={e.id}>
                {formatName(e)}
              </option>
            ))}
          </Select>
          <Select
            ref={projectRef}
            style={{
              width: '145px',
            }}
          >
            <option value="-1">{t('selectProject')}</option>
            {_.orderBy(project, ['name'], ['asc'])?.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </Select>
          <Flex gap={3} alignItems="center">
            {(filterState?.project || filterState?.employee) && (
              <Text
                textDecoration="underline"
                cursor="pointer"
                color="green.600"
                style={{ whiteSpace: 'nowrap' }}
                onClick={resetFilters}
              >
                resetFilter
              </Text>
            )}
            <Button onClick={handleClick}>search</Button>
            <Button
              disabled={!filterState.project && !filterState.employee}
              onClick={onToggle}
            >
              delete
            </Button>
          </Flex>
        </Flex>
      </Flex>
      <Flex w="100%" direction="column" alignItems="center">
        <Flex gap={3} alignItems="center" mb={3}>
          <IconButton
            aria-label="next"
            icon={<ArrowBackIcon />}
            onClick={() => setPage(Math.max(page - 1, 0))}
          />
          <Text size="lg">{page + 1}</Text>
          <IconButton
            aria-label="next"
            icon={<ArrowForwardIcon />}
            onClick={() =>
              setPage(
                Math.min(page + 1, Math.floor((recordCount?.count ?? 0) / 100)),
              )
            }
          />
        </Flex>
        <ListView
          data={textRecords}
          search=""
          searchFields={() => []}
          isLoading={isLoading}
          render={({ data }) => (
            <Grid templateColumns="repeat(2, 1fr)" gap={3}>
              {data.map((textRecord) => (
                <GridItem key={textRecord.id}>
                  <TextRecordListItem textRecord={textRecord} />
                </GridItem>
              ))}
            </Grid>
          )}
        />
        <Flex gap={3} alignItems="center" mt={3}>
          <IconButton
            aria-label="next"
            icon={<ArrowBackIcon />}
            onClick={() => setPage(Math.max(page - 1, 0))}
          />
          <Text size="lg">{page + 1}</Text>
          <IconButton
            aria-label="next"
            icon={<ArrowForwardIcon />}
            onClick={() =>
              setPage(
                Math.min(page + 1, Math.floor((recordCount?.count ?? 0) / 100)),
              )
            }
          />
        </Flex>
      </Flex>
    </>
  );
};
